import { Component, computed, ElementRef, Input, OnDestroy, OnInit, Signal, signal, Type, WritableSignal } from '@angular/core';
import { TraceDrawerType, TraceService } from '../../../services/trace.service';
import { BrowserSnifferService } from '../../../services/browser-sniffer.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { Subscription } from 'rxjs';
import { TraceRouteData } from '@tt-lib/models/trace-route-data';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Feature } from 'ol';
import { LineString, MultiLineString, Point } from 'ol/geom';
import { toObservable } from '@angular/core/rxjs-interop';
import { PopupService } from '@tt-lib/services/popup.service';
import { TraceSuggestionsConfigService } from '@tt-lib/services/trace-suggestions-config.service';

export type SuggestionsLangValuesType = string | { [langCode: string]: string };
export interface SuggestionsConfigData {
  add_az_index: boolean;
  route_id_prefix: string,
  route_id_suffix: SuggestionsLangValuesType,
  routes: SuggestionsConfigDataRoute[];
  description: SuggestionsLangValuesType
  form_options: SuggestionsConfigDataFormOptions;
};
export interface SuggestionsConfigDataRoute {
  title: SuggestionsLangValuesType,
  //currently there is no route_id per language, id's per language in one config file makes it too complex
  //maybe add multiple texts for every language in the route? or multiple configs per language?
  //route_id: SuggestionsLangValuesType,
  route_id: string,
  //to get these coordinates, you can open a viewer that uses the trace service, e.g. tmv, and add parameter 'logimport' to the URL before opening a route via the import tab
  //after import you will get the length of the route in meters and the coordinates from the (multi)linestring(s)/Point
  route_line_coordinates: number[] | number[][] | number[][][];
  route_epsg: string,//defaults to EPSG:3812
  az_index_title: SuggestionsLangValuesType,
  description: SuggestionsLangValuesType,
  //add or hide on map and in component list?
  show: boolean,
  preview_image_url: string,
  created: number,
  dist: number,
  upwardDist: number,
  time: number
};
export interface SuggestionsConfigDataFormOptions {
  default_layer: SuggestionsConfigDataFormOptionsDefaultLayer;
  route_centroids_on_map: SuggestionsConfigDataFormOptionsCentroids;
};
export interface SuggestionsConfigDataFormOptionsDefaultLayer {
  //one or two layer ids (when split/swipe is active)
  layer_id: string | string[]
  //add to form or hidden setting?
  show: boolean,
  form_checked: boolean,
  form_text: SuggestionsLangValuesType
};
export interface SuggestionsConfigDataFormOptionsCentroids {
  // font
  // font://walk-ferraris
  // http
  // https://mydomain.be/path/to/icon.png
  map_icon_url: string
  show: boolean,
  form_checked: boolean,
  form_text: SuggestionsLangValuesType
};

@Component({
  selector: 'tt-trace-suggestions',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    MatDividerModule,
    MatCardModule,
    TranslateModule,
    FormsModule,
    MatSlideToggleModule
  ],
  templateUrl: './trace-suggestions.component.html',
  styleUrls: ['../trace.scss'],
  standalone: true
})
export class TraceSuggestionsComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription;
  public lang: WritableSignal<string> = signal('en');

  //dynamic template attributes (setters/getters below)
  private _suggestionsTabTitleI8nKey: string = null;

  public isActive: WritableSignal<boolean> = signal(false);

  public highlightedSuggestionItemId: Signal<string> = computed(() => this.traceService.highlightedSuggestionRouteId());

  //private _appEnvironmentConfig: AppEnvironmentConfig = inject(TT_LIB_ENV_CONFIG);

  constructor(
    private _elRef: ElementRef,
    private _translateService: TranslateService,
    public browsersniffer: BrowserSnifferService,
    public traceService: TraceService,
    private _popupService: PopupService,
    public traceSuggestionsConfigService: TraceSuggestionsConfigService
  ) {
    this._subscriptions = new Subscription();
    this.lang.set(this._translateService.getCurrentLang());
    this._subscriptions.add(
      this._translateService.onLangChange
        .subscribe((langData) => {
          this.lang.set(langData.lang)
        }));
    this._subscriptions.add(
      toObservable(this.traceService.tabindex).subscribe((value: TraceDrawerType) => {
        this._checkForSuggestionsTabEvent();
      })
    );

    this._subscriptions.add(
      this._popupService.customEvent.subscribe((eventObj) => {
        if (
          this.traceService.isActive() &&
          eventObj &&
          eventObj.detail &&
          eventObj.detail.eventId &&
          eventObj.detail.eventId === 'TraceSuggestionInfo' &&
          eventObj.detail.data &&
          typeof eventObj.detail.data.routeId === 'string' &&
          eventObj.detail.data.routeId.trim().length > 0
        ) {
          if (this._popupService.isPopupAlreadyOnMap()) {
            this._popupService.hide();
          }
          this.traceService.openSuggestionsRoute(eventObj.detail.data.routeId, false);
        }
      })
    );
  }
  ngOnInit(): void {
    if (this.traceSuggestionsConfigService.configDownloadStatus() === 'init') {
      /*
      this.traceService.loadSuggestions(false, false);
      */      
      this.traceSuggestionsConfigService.downloadConfigData(false);
    }

    //in small version the subscription event was too early
    this._checkForSuggestionsTabEvent();

  }
  ngAfterViewInit() {
  }
  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  @Input()
  set suggestionsTabTitleI8nKey(val: string) {
    this._suggestionsTabTitleI8nKey = val ? val : null;
  }
  get suggestionsTabTitleI8nKey(): string {
    return this._suggestionsTabTitleI8nKey;
  }

  static checkConfigData(newData: any): SuggestionsConfigData {
    const wasEmptyObject = typeof newData !== 'object' || TraceRouteData.isEmptyObject(newData);
    let checkedData = !wasEmptyObject ? newData : { routes: [] };

    //needs to be set before checking route.az_index_title 
    if (typeof checkedData.add_az_index !== 'boolean') {
      checkedData.add_az_index = true;
    }

    if (typeof checkedData.route_id_prefix !== 'string' || checkedData.route_id_prefix.trim().length === 0) {
      checkedData.route_id_prefix = 'dummyprefix';
      if (!wasEmptyObject) {
        console.log("route_id_prefix is obligatory, please correct the config file, ")
      }
    }

    if (typeof checkedData.route_id_suffix !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(checkedData.route_id_suffix)) {
      checkedData.route_id_suffix = {};
    }

    if (Array.isArray(checkedData.routes)) {
      checkedData.routes.forEach((route: any) => {
        if ((typeof route.route_id !== 'string' || route.route_id.trim().length === 0) /*&& (typeof route.route_id !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(route.route_id))*/) {
          route.route_id = '';
          route.show = false;
        }
        if (!Array.isArray(route.route_line_coordinates)) {
          route.route_line_coordinates = [];
          //route.show = false;
        }
        if (typeof route.route_epsg !== 'string' || route.route_epsg.toLowerCase().indexOf("EPSG:") !== 0) {
          route.route_epsg = 'EPSG:3812';
        }
        if (typeof route.show !== 'boolean') {
          route.show = true;
        }
        if (checkedData.add_az_index && (typeof route.az_index_title !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(route.az_index_title))) {
          route.az_index_title = {};
        } else if (!checkedData.add_az_index && typeof route.az_index_title !== 'undefined') {
          route.az_index_title = {};
        }
        if (typeof route.description !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(route.description)) {
          route.description = {};
        }
        if (typeof route.title !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(route.title)) {
          route.title = {};
        }
        if (route.preview_image_url && typeof route.preview_image_url !== 'string') {
          route.preview_image_url = '';
        }
        if (typeof route.created !== 'number' || route.created < 1714000000000) {
          route.created = -1;
        }
        if (typeof route.dist !== 'number' || route.dist < -1) {
          route.dist = -1;
        }
        if (typeof route.upwardDist !== 'number' || route.upwardDist < -1) {
          route.upwardDist = -1;
        }
        if (typeof route.time !== 'number' || route.time < -1) {
          route.time = -1;
        }
      });
    } else {
      checkedData.routes = [];
    }
    if (typeof checkedData.description !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(checkedData.description)) {
      checkedData.description = {};
    }
    if (typeof checkedData.form_options !== 'object') {
      checkedData.form_options = <SuggestionsConfigDataFormOptions>{};
    }
    if (typeof checkedData.form_options.default_layer !== 'object') {
      checkedData.form_options.default_layer = <SuggestionsConfigDataFormOptionsDefaultLayer>{};
    }
    if ((typeof checkedData.form_options.default_layer.layer_id !== 'string' || checkedData.form_options.default_layer.layer_id.trim().length === 0) && (!Array.isArray(checkedData.form_options.default_layer.layer_id) || checkedData.form_options.default_layer.layer_id.length === 0 || typeof checkedData.form_options.default_layer.layer_id[0] !== 'string' || checkedData.form_options.default_layer.layer_id[0].trim().length === 0)) {
      checkedData.form_options.default_layer.layer_id = 'crossborder.topo_bw';
    } else if (Array.isArray(checkedData.form_options.default_layer.layer_id) && checkedData.form_options.default_layer.layer_id.length > 1 && (typeof checkedData.form_options.default_layer.layer_id[1] !== 'string' || checkedData.form_options.default_layer.layer_id[1].trim().length === 0)) {
      checkedData.form_options.default_layer.layer_id = checkedData.form_options.default_layer.layer_id[0];
    }
    if (typeof checkedData.form_options.default_layer.show !== 'boolean') {
      checkedData.form_options.default_layer.show = true;
    }
    if (typeof checkedData.form_options.default_layer.form_checked !== 'boolean') {
      checkedData.form_options.default_layer.form_checked = true;
    }
    if (typeof checkedData.form_options.default_layer.form_text !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(checkedData.form_options.default_layer.form_text)) {
      checkedData.form_options.default_layer.form_text = {
        "fr": "passer à CartoWeb-TOPO-GREY",
        "nl": "overschakelen naar CartoWeb-TOPO-GREY",
        "en": "switch to CartoWeb-TOPO-GREY",
        "de": "Zur CartoWeb-TOPO-GREY wechseln"
      };
    }
    if (typeof checkedData.form_options.route_centroids_on_map !== 'object') {
      checkedData.form_options.route_centroids_on_map = <SuggestionsConfigDataFormOptionsCentroids>{};
    }
    if (typeof checkedData.form_options.route_centroids_on_map.map_icon_url !== 'string' || checkedData.form_options.route_centroids_on_map.map_icon_url.trim().length === 0) {
      checkedData.form_options.route_centroids_on_map.map_icon_url = 'font://default';//value 'font://default' maps to MarkerService.markerCodes['default']
    }
    if (typeof checkedData.form_options.route_centroids_on_map.show !== 'boolean') {
      checkedData.form_options.route_centroids_on_map.show = true;
    }
    if (typeof checkedData.form_options.route_centroids_on_map.form_checked !== 'boolean') {
      checkedData.form_options.route_centroids_on_map.form_checked = true;
    }
    if (typeof checkedData.form_options.route_centroids_on_map.form_text !== 'object' || !TraceSuggestionsComponent.hasTextForLanguages(checkedData.form_options.route_centroids_on_map.form_text)) {
      checkedData.form_options.route_centroids_on_map.form_text = {
        "fr": "afficher tous les itinéraires sur la carte",
        "nl": "alle routes op de kaart weergeven",
        "en": "show all routes on the map",
        "de": "Alle Routen auf der Karte anzeigen"
      };
    }
    return <SuggestionsConfigData>checkedData;
  }

  private _checkForSuggestionsTabEvent() {
    this.isActive.set(this.traceService.tabindex() === TraceDrawerType.SUGGESTIONS_ROUTE);
  }

  getOrderedConfigDataRoutes(orderBy: 'title' | 'az_index_title' | 'created', orderAscending: boolean) {
    return this.traceSuggestionsConfigService.configData()
      && Array.isArray(this.traceSuggestionsConfigService.configData().routes)
      && this.traceSuggestionsConfigService.configData().routes.length > 0 ?

      this.traceSuggestionsConfigService.configData().routes.slice(0).sort((a: SuggestionsConfigDataRoute, b: SuggestionsConfigDataRoute) => {
        if ((/^(title|az_index_title)$/.test(orderBy))) {
          const a_ = this.findTextForCurrentLanguage(orderBy === 'title' ? a.title : a.az_index_title, -1);
          const b_ = this.findTextForCurrentLanguage(orderBy === 'title' ? b.title : b.az_index_title, -1);
          if (orderAscending) {
            return a_ > b_ ? 1 : b_ > a_ ? -1 : 0;
          } else {
            return a_ < b_ ? 1 : b_ < a_ ? -1 : 0;
          }
        } else {
          //make created mandatory?
          if (orderAscending) {
            return (a.created > b.created) ? 1 : ((b.created > a.created) ? -1 : 0);
          } else {
            return (a.created < b.created) ? 1 : ((b.created < a.created) ? -1 : 0);
          }
        }
      })
      :
      [];
  }

  getSuggestionsCount() {
    let suggestionsCount = 0;
    if (this.traceSuggestionsConfigService.configData() && this.traceSuggestionsConfigService.configData().routes && this.traceSuggestionsConfigService.configData().routes.length > 0) {
      this.traceSuggestionsConfigService.configData().routes.forEach((route: SuggestionsConfigDataRoute) => {
        suggestionsCount += route.show ? 1 : 0;
      })
    }
    return suggestionsCount;
  }

  formatRouteDistance(distance: number, isUpwardDistance: boolean): string {
    return typeof distance === "number" && distance > -1 ? TraceRouteData.formatRouteLength(distance, true, '0 <span>m</span>') : isUpwardDistance ? '_' : '0 <span>m</span>';
  }
  formatRouteTime(time: number) {
    return typeof time === "number" && time >= 0 ? TraceRouteData.formatRouteTime(time, '-') : '-';
  }

  scrollToSuggestionsItem(routeId: string) {
    //css class in template, that sets pointer events to none?
    //this.scrollingIntoView.set(true);
    const routeEl = this._elRef.nativeElement.querySelector(
      '#' + routeId
    );
    const scrollContainer = document.querySelector('.trace-body');
    const scrollPosition =
      routeEl.getBoundingClientRect().top +
      scrollContainer.scrollTop -
      scrollContainer.getBoundingClientRect().top - 24; //24 offset

    if (this.browsersniffer.isPhone()) {
      routeEl?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      });
    } else {
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }


    /*
     window.setTimeout(() => {
      this.scrollingIntoView.set(false);
    }, routeEl ? 1000 : 0);
    */
  }

  static findTextForRouteId(routes: SuggestionsConfigDataRoute[], routeId: string, findTitle: boolean, lang: string) {
    let result = null;
    if (Array.isArray(routes) && typeof routeId === 'string') {
      routes.every(route => {
        if (routeId === route.route_id) {
          result = this.findTextForRequestedLanguage(findTitle ? route.title : route.description, -1, lang ? lang : 'en');
          return false;
        }
        return true;
      });
    }
    return result;
  }

  findTextForCurrentLanguage(stringValuePerLangObject: SuggestionsLangValuesType, maxLength: number) {
    return TraceSuggestionsComponent.findTextForRequestedLanguage(stringValuePerLangObject, maxLength, this.lang());
  }

  static findTextForRequestedLanguage(stringValuePerLangObject: SuggestionsLangValuesType, maxLength: number, lang: string) {
    let textForLanguage = null;

    if (typeof stringValuePerLangObject === 'object') {
      lang = typeof lang === 'string' && lang.length > 0 ? lang : 'en';
      if (stringValuePerLangObject[lang]) {
        textForLanguage = stringValuePerLangObject[lang];
      } else if (lang !== 'en' && stringValuePerLangObject['en']) {
        textForLanguage = stringValuePerLangObject[lang];
      } else {
        for (let key in stringValuePerLangObject) {
          if (typeof stringValuePerLangObject[key] === 'string' && stringValuePerLangObject[key].length > 0) {
            textForLanguage = stringValuePerLangObject[key];
            break;
          }
        }
      }

      if (typeof maxLength === 'number' && maxLength > 0 && textForLanguage && textForLanguage.length > (maxLength + 3)) {
        textForLanguage = textForLanguage.substring(0, maxLength) + '...';
      }
    } else if (typeof stringValuePerLangObject === 'string' && stringValuePerLangObject.length > 0) {
      textForLanguage = stringValuePerLangObject;
    }

    return textForLanguage;
  }

  static findRouteIdSuffixForRequestedLanguage(stringValuePerLangObject: SuggestionsLangValuesType, lang: string) {
    //no fallback to other language in object
    return typeof stringValuePerLangObject === 'object'
      && typeof lang === 'string'
      && typeof stringValuePerLangObject[lang] === 'string' ? stringValuePerLangObject[lang].trim() : '';
  }

  static findDistanceForRouteId(routes: SuggestionsConfigDataRoute[], routeId: string, findTotalDistance: boolean) {
    let result = -1;

    if (Array.isArray(routes) && typeof routeId === 'string') {
      routes.every(route => {
        if (routeId === route.route_id) {
          const tempDist = findTotalDistance ? route.dist : route.upwardDist;
          result = typeof tempDist === 'number' ? tempDist : -1;
          return false;
        }
        return true;
      });
    }

    return result;
  }

  static hasTextForLanguages(stringValuePerLangObject: SuggestionsLangValuesType) {
    let textForLanguage = null;
    if (typeof stringValuePerLangObject === 'object') {
      for (let key in stringValuePerLangObject) {
        if (/^(en|nl|fr|de)$/.test(key) && typeof stringValuePerLangObject[key] === 'string' && stringValuePerLangObject[key].length > 0) {
          textForLanguage = stringValuePerLangObject[key];
          break;
        }
      }
    } else if (typeof stringValuePerLangObject === 'string' && stringValuePerLangObject.length > 0) {
      textForLanguage = stringValuePerLangObject;
    }
    return textForLanguage !== null;
  }

  static getGeometryTypeForCoordinates(coords: number[] | number[][] | number[][][]) {
    if (Array.isArray(coords) && coords.length > 0) {
      const first = coords[0];
      if (typeof first === 'number') {
        return 'Point';
      } else {
        if (Array.isArray(first[0])) {
          return 'MultiLineString';
        } else if (typeof first[0] === 'number') {
          return 'LineString';
        }
      }
    }
    return null;
  }

  static convertConfigDataToFeatures(configData: SuggestionsConfigData, currentMapProjectionCode: string) {
    const features: Feature[] = []
    configData?.routes.forEach((route: SuggestionsConfigDataRoute) => {

      if (route.show && route.route_line_coordinates.length > 0) {
        const geometryType = TraceSuggestionsComponent.getGeometryTypeForCoordinates(route.route_line_coordinates);
        if (geometryType) {
          const geometry = geometryType === 'MultiLineString' ? new MultiLineString(<number[][][]>route.route_line_coordinates) : geometryType === 'LineString' ? new LineString(<number[][]>route.route_line_coordinates) : new Point(<number[]>route.route_line_coordinates);
          if (currentMapProjectionCode !== route.route_epsg) {
            geometry.transform(route.route_epsg, currentMapProjectionCode);
          }
          const feature = new Feature(geometry);
          feature.set("route_id", route.route_id);
          features.push(feature);
        }
      }
    });
    return features;
  }
}
